import { useLocation, useNavigate } from "react-router-dom";
import { FileText, CheckSquare, Phone, Calendar, MoreHorizontal, Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCanAccessModule } from "@/hooks/useModulePermissions";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { canAccess: canAccessCases } = useCanAccessModule('cases');
  const { canAccess: canAccessPatients } = useCanAccessModule('patients');
  const { canAccess: canAccessTasks } = useCanAccessModule('tasks');
  const { canAccess: canAccessCallLogbook } = useCanAccessModule('call_logbook');

  // Fetch unread notifications count for badge
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["unread-notifications-count-nav", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      const { count } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("is_read", false);
      return count || 0;
    },
    enabled: !!user?.id,
    staleTime: 30000,
    refetchInterval: 60000,
  });

  // Mobile navigation - lean and focused
  const navItems = [
    {
      path: "/appointments",
      label: "Schedule",
      icon: Calendar,
      show: canAccessPatients,
    },
    {
      path: "/tasks",
      label: "Tasks",
      icon: CheckSquare,
      show: canAccessTasks,
    },
    {
      path: "/",
      label: "Lab Work",
      icon: FileText,
      show: canAccessCases,
    },
    {
      path: "/call-logbook",
      label: "Calls",
      icon: Phone,
      show: canAccessCallLogbook,
    },
    {
      path: "/more",
      label: "More",
      icon: MoreHorizontal,
      show: true, // Always show More
      badge: unreadCount,
    },
  ].filter(item => item.show);

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-t shadow-lg">
      <div 
        className="flex items-center justify-around px-1" 
        style={{ 
          height: 'calc(4rem + env(safe-area-inset-bottom, 0px))',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)'
        }}
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.path === "/more" 
            ? location.pathname === "/more"
            : location.pathname === item.path;
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={cn(
                "relative flex flex-col items-center justify-center gap-0.5 py-2 px-2 rounded-xl transition-all min-w-[56px] min-h-[56px] flex-1 active:scale-95",
                isActive
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              <Icon className={cn("h-6 w-6", isActive && "scale-110")} />
              <span className="text-[10px] font-medium leading-none truncate max-w-[56px]">{item.label}</span>
              
              {/* Badge for More menu with unread notifications */}
              {item.badge !== undefined && item.badge > 0 && (
                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full px-1">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
